/**
 * CALL EDIT FORM BODY — Form di modifica inline dei campi di una Call.
 *
 * Embed dentro AccordionDetails di ActivityTimeline. Pattern coerente
 * con TaskEditFormBody/NoteEditFormBody: EditableField inline, doppio
 * click per edit, blur/Enter per commit.
 */
import { useTranslation } from 'react-i18next';
import { EditableField, type EditableValue } from '../../components/EditableField';
import { RichTextField } from '../../components/RichTextField';
import { useAppDispatch } from '../../store/hooks';
import { useToast } from '../../components/useToast';
import { updateCall } from './callThunks';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { useAssociatedRecordsBinder } from '../../components/useAssociatedRecordsBinder';
import { ActivityTools } from '../../components/ActivityTools';
import { CALL_DIRECTIONS, CALL_STATUSES, type Call, type CallCreatePayload, type CallUpdatePayload } from './callTypes';

interface CallEditFormBodyProps {
  call: Call;
}

export function CallEditFormBody({ call }: CallEditFormBodyProps) {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { t } = useTranslation();

  const callFqn = getEntityFqn('Call');
  const {
    records: currentlyLinked,
    onRecordsChange: handleRecordsChange,
    recordsSaving,
    pickerProps: recordsPickerProps,
  } = useAssociatedRecordsBinder({
    mode: 'edit',
    entityFqn: callFqn ?? '',
    entityId: call.callId,
  });

  const commitField =
    (field: keyof CallCreatePayload) =>
    async (newValue: EditableValue) => {
      try {
        const payload = { callId: call.callId, [field]: newValue } as CallUpdatePayload;
        await dispatch(updateCall(payload)).unwrap();
        toast({ severity: 'success', message: t('common.saved') });
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        throw err;
      }
    };

  return (
    <div className="call-edit-body">
      {/* Una sola griglia a tre colonne per le due righe, così le colonne restano
          allineate fra loro: direzione/esito sopra, data e interno sotto. L'interno
          vale in entrambe le direzioni: in entrata è chi ha risposto, in uscita chi
          ha chiamato. Se il centralino non lo trasmette, i campi restano vuoti. */}
      <div className="task-edit-body__grid task-edit-body__grid--3col">
        <div className="field-row">
          <div className="field-row__label text-xs text-muted">{t('call.direction')}</div>
          <EditableField
            value={call.direction}
            variant={{ kind: 'select', options: CALL_DIRECTIONS }}
            required
            onCommit={commitField('direction')}
            label={t('call.direction')}
          />
        </div>
        <div className="field-row">
          <div className="field-row__label text-xs text-muted">{t('call.status')}</div>
          <EditableField
            value={call.status}
            variant={{ kind: 'select', options: CALL_STATUSES }}
            required
            onCommit={commitField('status')}
            label={t('call.status')}
          />
        </div>
        {/* Terza colonna della prima riga: vuota, tiene l'allineamento. */}
        <div />

        <div className="field-row">
          <div className="field-row__label text-xs text-muted">{t('call.callDate')}</div>
          <EditableField
            value={call.callDate}
            variant={{ kind: 'datetime' }}
            required
            onCommit={commitField('callDate')}
            label={t('call.callDate')}
          />
        </div>

        <div className="field-row">
          <div className="field-row__label text-xs text-muted">
            {t('call.internalExtension')}
          </div>
          {/* Sola lettura: lo scrive il centralino, non si corregge a mano. Il
              commit non viene mai invocato, ma il contratto lo richiede. */}
          <EditableField
            value={call.internalExtension ?? ''}
            variant={{ kind: 'text' }}
            readOnly
            onCommit={async () => {}}
            label={t('call.internalExtension')}
          />
        </div>
        <div className="field-row">
          <div className="field-row__label text-xs text-muted">
            {t('call.internalName')}
          </div>
          <EditableField
            value={call.internalName ?? ''}
            variant={{ kind: 'text' }}
            onCommit={commitField('internalName')}
            label={t('call.internalName')}
          />
        </div>
      </div>

      <div className="field-row">
        <div className="field-row__label text-xs text-muted">{t('call.description')}</div>
        <RichTextField
          value={call.description}
          onCommit={commitField('description')}
          placeholder={t('call.descriptionPlaceholder')}
          required
        />
      </div>

      {/* actionItems: facoltativo. Visibile ed editabile solo se valorizzato
          (popolato da automazione/import lato backend). Vuoto -> non renderizzato. */}
      {call.actionItems && (
        <div className="field-row">
          <div className="field-row__label text-xs text-muted">{t('call.actionItems')}</div>
          <EditableField
            value={call.actionItems}
            variant={{ kind: 'textarea', rows: 4 }}
            onCommit={commitField('actionItems')}
            label={t('call.actionItems')}
          />
        </div>
      )}

      <ActivityTools
        {...recordsPickerProps}
        records={currentlyLinked}
        onChange={(val) => handleRecordsChange?.(val)}
        recordsSaving={recordsSaving}
        entityFqn={callFqn ?? ''}
        entityId={call.callId}
      />
    </div>
  );
}
